import { Diff } from './diffTypes'
import {
  generateChangeText,
  repeatTabs,
  sortMapToArray,
} from './textDiffFormatting'

export type AssumptionSetDiffWithProps = Diff & {
  assumptionSetName: string
}

export type AssumptionSetAssumptionProps = {
  state: string
  name: string
  assumptionSetName: string
}

export type ChangedAssumptionSetAssumptionProps = AssumptionSetDiffWithProps & {
  name: string
}

export type RequirementProps = {
  assumptionSetName: string
  name: string
}

export type ChangedRequirementProps = AssumptionSetDiffWithProps & {
  name: string
  state: string
  label: string
}

export type AssumptionSetDiff = {
  name?: Diff
  changedAssumptions?: Array<Diff & { name: string }>
  addedAssumptions?: Array<{ name: string; state: string }>
  deletedAssumptions?: Array<{ name: string; state: string }>
  changedRequirements?: Array<{
    name: string
    state: string
    changes: Array<Diff & { label: string }>
  }>
  addedRequirements?: Array<{ name: string }>
  deletedRequirements?: Array<{ name: string }>
}

export type AssumptionSetsDiff = Map<string, AssumptionSetDiff>

export class AssumptionSetDiffBuilder {
  private addedAssumptionSets: Array<string> = []
  private deletedAssumptionSets: Array<string> = []
  private changedAssumptionSets: AssumptionSetsDiff = new Map()

  addAddedAssumptionSet(assumptionSetName: string): void {
    this.addedAssumptionSets.push(assumptionSetName)
  }

  addDeletedAssumptionSet(assumptionSetName: string): void {
    this.deletedAssumptionSets.push(assumptionSetName)
  }

  addChangedAssumptionSetName({
    from,
    to,
    assumptionSetName,
  }: AssumptionSetDiffWithProps): void {
    const existingAssumptionSet =
      this.changedAssumptionSets.get(assumptionSetName)
    this.changedAssumptionSets.set(assumptionSetName, {
      ...existingAssumptionSet,
      name: { from, to },
    })
  }

  addAddedAssumptionSetAssumption({
    assumptionSetName,
    state,
    name,
  }: AssumptionSetAssumptionProps): void {
    const existingAssumptionSet =
      this.changedAssumptionSets.get(assumptionSetName)
    this.changedAssumptionSets.set(assumptionSetName, {
      ...existingAssumptionSet,
      addedAssumptions: [
        ...(existingAssumptionSet?.addedAssumptions ?? []),
        { name, state },
      ],
    })
  }

  addDeletedAssumptionSetAssumption({
    assumptionSetName,
    state,
    name,
  }: AssumptionSetAssumptionProps): void {
    const existingAssumptionSet =
      this.changedAssumptionSets.get(assumptionSetName)
    this.changedAssumptionSets.set(assumptionSetName, {
      ...existingAssumptionSet,
      deletedAssumptions: [
        ...(existingAssumptionSet?.deletedAssumptions ?? []),
        { name, state },
      ],
    })
  }

  addChangedAssumptionSetAssumption({
    assumptionSetName,
    from,
    to,
    name,
  }: ChangedAssumptionSetAssumptionProps): void {
    const existingAssumptionSet =
      this.changedAssumptionSets.get(assumptionSetName)
    this.changedAssumptionSets.set(assumptionSetName, {
      ...existingAssumptionSet,
      changedAssumptions: [
        ...(existingAssumptionSet?.changedAssumptions ?? []),
        { from, to, name },
      ],
    })
  }

  addAddedRequirement({ assumptionSetName, name }: RequirementProps): void {
    const existingAssumptionSet =
      this.changedAssumptionSets.get(assumptionSetName)
    this.changedAssumptionSets.set(assumptionSetName, {
      ...existingAssumptionSet,
      addedRequirements: [
        ...(existingAssumptionSet?.addedRequirements ?? []),
        { name },
      ],
    })
  }

  addDeletedRequirement({ assumptionSetName, name }: RequirementProps): void {
    const existingAssumptionSet =
      this.changedAssumptionSets.get(assumptionSetName)
    this.changedAssumptionSets.set(assumptionSetName, {
      ...existingAssumptionSet,
      deletedRequirements: [
        ...(existingAssumptionSet?.deletedRequirements ?? []),
        { name },
      ],
    })
  }

  addChangedRequirement({
    assumptionSetName,
    from,
    to,
    name,
    state,
    label,
  }: ChangedRequirementProps): void {
    const existingAssumptionSet =
      this.changedAssumptionSets.get(assumptionSetName)
    const currentRequirement = existingAssumptionSet?.changedRequirements?.find(
      (requirement) => requirement.name === name && requirement.state === state,
    )
    const existingRequirementsWithoutCurrent =
      existingAssumptionSet?.changedRequirements?.filter(
        (requirement) =>
          requirement.name !== name || requirement.state !== state,
      ) ?? []
    this.changedAssumptionSets.set(assumptionSetName, {
      ...existingAssumptionSet,
      changedRequirements: [
        ...existingRequirementsWithoutCurrent,
        {
          name,
          state,
          changes: [
            ...(currentRequirement?.changes ?? []),
            { from, to, label },
          ],
        },
      ],
    })
  }

  renderAddedAssumptionSetList(): string {
    return this.generateAssumptionSetList(this.addedAssumptionSets)
  }

  renderDeletedAssumptionSetList(): string {
    return this.generateAssumptionSetList(this.deletedAssumptionSets)
  }

  renderAssumptionSetChanges(): string {
    return this.generateAssumptionSetChanges()
  }

  private generateAssumptionSetList = (assumptionSets: string[]): string => {
    if (assumptionSets.length === 0) {
      return `${repeatTabs(1)}None`
    }
    return assumptionSets
      .map((assumptionSet) => `${repeatTabs(1)}${assumptionSet}`)
      .join('\n')
  }

  private generateAssumptionSetChanges = (): string => {
    const assumptionSetsChanges = sortMapToArray(this.changedAssumptionSets)
    if (assumptionSetsChanges.length === 0) {
      return `${repeatTabs(1)}None`
    }
    return assumptionSetsChanges
      .map(([assumptionSetName, diffs]) => {
        const diffTexts = []
        if (diffs.name) {
          diffTexts.push(
            generateChangeText({
              from: diffs.name.from,
              to: diffs.name.to,
              label: 'Name',
              tabs: 2,
            }),
          )
        }
        if (diffs.deletedAssumptions) {
          diffTexts.push(
            `${repeatTabs(2)}Deleted assumptions:\n${diffs.deletedAssumptions
              .map(({ name }) => `${repeatTabs(3)}${name}`)
              .join('\n')}`,
          )
        }
        if (diffs.addedAssumptions) {
          diffTexts.push(
            `${repeatTabs(2)}Added assumptions:\n${diffs.addedAssumptions
              .map(({ name }) => `${repeatTabs(3)}${name}`)
              .join('\n')}`,
          )
        }
        if (diffs.changedAssumptions) {
          diffTexts.push(
            `${repeatTabs(2)}Changed assumptions:\n${diffs.changedAssumptions
              .map(
                ({ name, from, to }) =>
                  `${repeatTabs(3)}${name}\n${generateChangeText({
                    from,
                    to,
                    label: `State`,
                    tabs: 4,
                  })}`,
              )
              .join('\n')}`,
          )
        }
        if (diffs.deletedRequirements) {
          diffTexts.push(
            `${repeatTabs(2)}Deleted requirements:\n${diffs.deletedRequirements
              .map(({ name }) => `${repeatTabs(3)}${name}`)
              .join('\n')}`,
          )
        }
        if (diffs.addedRequirements) {
          diffTexts.push(
            `${repeatTabs(2)}Added requirements:\n${diffs.addedRequirements
              .map(({ name }) => `${repeatTabs(3)}${name}`)
              .join('\n')}`,
          )
        }
        if (diffs.changedRequirements) {
          diffTexts.push(
            `${repeatTabs(2)}Changed requirements:\n${diffs.changedRequirements
              .map(
                ({ name, state, changes }) =>
                  `${repeatTabs(3)}${name}-${state}\n${changes
                    .map(({ from, to, label }) =>
                      generateChangeText({
                        from,
                        to,
                        label: label,
                        tabs: 4,
                      }),
                    )
                    .join('\n')}`,
              )
              .join('\n')}`,
          )
        }

        return `${repeatTabs(1)}AssumptionSet: ${assumptionSetName}\n${diffTexts.join('\n')}`
      })
      .join('\n')
  }
}
